import { replayProtocolFromHttpVersion } from '@terra/shared-utils';
import type { StoredHarDisplayEntry } from '@terra/shared-utils';
import { useLayoutStore } from '../store/layoutStore';
import {
  extractConnectionTarget,
  formatRequestToText,
  type ConnectionTarget,
} from './httpFormatter';
import {
  estimateBase64ByteSize,
  decodeBinaryBodyForDisplay,
  encodeBinaryBody,
  encodeDisplayBodyForTransport,
  getContentTypeHeader,
  isMultipartContentType,
  parseBinaryBody,
  type BodyEncoding,
} from '../electron-shared/utils/binary-body';
import type { ReplaySession } from '../store/replaySessionStore';
import { buildProxyGrpcHarDisplayEntry } from './proxyGrpcDisplay.utils';
import type { HttpRequest } from '../types/http-request';

export type ReplayOpenRequestInput = {
  method: string;
  url: string;
  headers?: Record<string, string | string[]>;
  rawHeaders?: string[];
  body?: string;
  bodyEncoding?: BodyEncoding;
  httpVersion?: string;
  connectionTarget?: ConnectionTarget;
  grpcHarDisplay?: StoredHarDisplayEntry;
  responseStatus?: number;
  responseStatusMessage?: string;
  responseHeaders?: Record<string, string | string[]>;
  responseBody?: string;
  responseHttpVersion?: string;
};

export type ReplayOpenRequestOptions = {
  sessionId?: string;
};

type BuildReplaySessionPayloadParams = {
  requestData: ReplayOpenRequestInput;
  applicationId: string | null;
  collectionId: string;
  looksLikeDisplayedBinaryMultipart: (body?: string) => boolean;
};

export type ReplaySessionPayload = Omit<
  ReplaySession,
  'id' | 'collectionId' | 'activeCredential'
> & {
  collectionId: string;
  activeCredential: null;
};

export function focusReplayView(): void {
  const {
    setSidebarTab,
    openMainContentTab,
    setActiveMainContentTab,
    activeMainContentTabId,
    tabNavigationByTabId,
    mainContentTabs,
  } = useLayoutStore.getState();

  setSidebarTab('replay');

  const activeNavigation = activeMainContentTabId
    ? tabNavigationByTabId[activeMainContentTabId]
    : undefined;
  const activePageEntry =
    activeNavigation?.entries[activeNavigation.currentIndex];

  if (activePageEntry?.type === 'replay') {
    return;
  }

  const existingReplayTab = mainContentTabs.find(
    (tab) => tab.type === 'replay',
  );

  if (existingReplayTab) {
    setActiveMainContentTab(existingReplayTab.id);

    return;
  }

  openMainContentTab({
    id: 'replay',
    title: 'Replay',
    type: 'replay',
  });
}

export const buildReplayGrpcHarDisplay = ({
  requestData,
  transportBody,
}: {
  requestData: ReplayOpenRequestInput;
  transportBody: string;
}): StoredHarDisplayEntry | undefined => {
  if (requestData.grpcHarDisplay) {
    return requestData.grpcHarDisplay;
  }

  let host = '';

  try {
    host = new URL(requestData.url).host;
  } catch {
    host = '';
  }

  const proxyRequest: HttpRequest = {
    id: 'replay-open',
    sequentialId: 0,
    method: requestData.method,
    url: requestData.url,
    host,
    headers: requestData.headers ?? {},
    rawHeaders: requestData.rawHeaders,
    body: transportBody,
    timestamp: new Date(),
    source: 'replay',
    httpVersion: requestData.httpVersion ?? 'HTTP/1.1',
    status: requestData.responseStatus,
    responseStatusMessage: requestData.responseStatusMessage,
    responseHeaders: requestData.responseHeaders,
    responseBody: requestData.responseBody,
    responseHttpVersion: requestData.responseHttpVersion,
  };

  return buildProxyGrpcHarDisplayEntry({
    request: proxyRequest,
    requestBody: transportBody,
    responseBody: requestData.responseBody,
  });
};

export function buildReplayGrpcHarDisplayFromSession({
  session,
  currentResponse,
}: {
  session: Pick<ReplaySession, 'request'>;
  currentResponse?: ReplaySession['responseHistory'][number];
}): StoredHarDisplayEntry | undefined {
  return buildReplayGrpcHarDisplay({
    requestData: {
      method: session.request.method,
      url: session.request.url,
      headers: session.request.headers,
      rawHeaders: session.request.rawHeaders,
      body: session.request.body,
      bodyEncoding: session.request.bodyEncoding,
      responseStatus: currentResponse?.statusCode,
      responseStatusMessage: currentResponse?.statusMessage,
      responseHeaders: currentResponse?.headers,
      responseBody: currentResponse?.body,
      responseHttpVersion: currentResponse?.httpVersion,
    },
    transportBody: session.request.body ?? '',
  });
}

export function buildReplaySessionPayload({
  requestData,
  applicationId,
  collectionId,
  looksLikeDisplayedBinaryMultipart,
}: BuildReplaySessionPayloadParams): ReplaySessionPayload {
  let displayPath = requestData.url;

  try {
    const url = new URL(requestData.url);

    displayPath = url.pathname;
  } catch {
    displayPath = requestData.url.split('?')[0] || requestData.url;
  }

  const contentType = getContentTypeHeader(requestData.headers);
  const isMultipartRequest = isMultipartContentType(contentType);
  const shouldReencodeDisplayedMultipart =
    isMultipartRequest &&
    !requestData.bodyEncoding &&
    looksLikeDisplayedBinaryMultipart(requestData.body);

  const transportBody = shouldReencodeDisplayedMultipart
    ? parseBinaryBody(encodeDisplayBodyForTransport(requestData.body || ''))
        .body
    : requestData.body || '';
  const transportBodyEncoding = shouldReencodeDisplayedMultipart
    ? 'base64'
    : requestData.bodyEncoding;

  const request = {
    method: requestData.method,
    url: requestData.url,
    headers: requestData.headers || {},
    rawHeaders: requestData.rawHeaders,
    body: transportBody,
    bodyEncoding: transportBodyEncoding,
    httpVersion: requestData.httpVersion,
  };

  let editorBody: string | undefined;

  if (transportBodyEncoding === 'base64' && request.body) {
    const binaryPrefixed = encodeBinaryBody(
      Buffer.from(request.body, 'base64'),
    );
    const decoded = decodeBinaryBodyForDisplay(binaryPrefixed);

    editorBody =
      decoded !== binaryPrefixed
        ? decoded
        : `[Binary body: ${estimateBase64ByteSize(request.body)} bytes, base64-encoded — will be sent as raw binary]`;
  }

  const displayRequest = editorBody
    ? { ...request, body: editorBody }
    : request;
  const grpcHarDisplay = buildReplayGrpcHarDisplay({
    requestData,
    transportBody,
  });

  return {
    title: displayPath,
    collectionId,
    applicationId: applicationId ?? undefined,
    request,
    responseHistory: [],
    currentResponseIndex: -1,
    requestData: formatRequestToText(displayRequest),
    protocol: replayProtocolFromHttpVersion(requestData.httpVersion),
    connectionTarget:
      requestData.connectionTarget ?? extractConnectionTarget(requestData.url),
    activeCredential: null,
    grpcHarDisplay,
  };
}
